import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useHistory } from '@treams/treams-routes';

import * as graphql from 'generated/graphql';
import CheckmarkIcon from 'packages/kakapo-ui-kit/assets/icons/CheckmarkIcon';
import { useWorkspace } from 'packages/kakapo-ui-services/hooks/useWorkspace';
import { SectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';

const CheckpointContainer = styled.div`
	border-bottom: 1px solid #f0f1f2;
	padding-top: 20px;
	padding-bottom: 20px;
	display: flex;
	align-items: center;
`;

const CheckmarkContainer = styled.div<{ completed?: boolean }>`
	background: ${(props) =>
		props.completed ? props.theme.colors.greenDark : props.theme.colors.slateBlue};
	border-radius: 16px;
	width: 32px;
	height: 32px;
	margin-right: 10px;
	flex-grow: 0;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const CheckpointTask = styled.div`
	flex-grow: 1;
	flex-shrink: 1;
`;

const CheckpointTaskTitle = styled.div<{ completed?: boolean }>`
	color: ${(props) => props.theme.colors.slateBlue};
	opacity: ${(props) => (props.completed ? 0.5 : 1)};
	text-decoration: ${(props) => (props.completed ? 'line-through' : 'none')};
`;

const CheckpointTaskAction = styled.a`
	color: ${(props) => props.theme.colors.primary};
	text-decoration: underline;
	cursor: pointer;

	&:hover {
		text-decoration: none;

		&:active {
			color: black;
		}
	}
`;

function Checkpoint(props: {
	index: number;
	completed?: boolean;
	url: string;
	title: string;
	action: string;
}) {
	const { t } = useTranslation();
	const history = useHistory();

	return (
		<CheckpointContainer>
			<CheckmarkContainer completed={props.completed}>
				{props.completed && <CheckmarkIcon />}
			</CheckmarkContainer>
			<CheckpointTask>
				<CheckpointTaskTitle completed={props.completed}>
					{t('checkpoints.step')} {props.index + 1}. {props.title}
				</CheckpointTaskTitle>
				<CheckpointTaskAction onClick={() => history.push(props.url)}>
					{props.action}
				</CheckpointTaskAction>
			</CheckpointTask>
		</CheckpointContainer>
	);
}

export default function Checkpoints(props: { checkpoints: { checkpoint: string; url: string }[] }) {
	const { t } = useTranslation();

	const workspace = useWorkspace();
	const completed = graphql.useCheckpointsQuery({
		variables: {
			workspaceID: workspace.id,
		},
	});

	if (completed.loading || !completed.data) {
		return null;
	}

	const completedAll = props.checkpoints.every((item) => {
		return completed.data?.workspace.checkpoints?.some(
			(checkpoint) => checkpoint?.key === item.checkpoint && checkpoint?.completedAt,
		);
	});

	if (completedAll) {
		return null;
	}

	return (
		<>
			<SectionTitle>{t('checkpoints.title')}</SectionTitle>
			{props.checkpoints.map((item, index) => (
				<Checkpoint
					{...item}
					key={index}
					index={index}
					title={t(`checkpoints.${item.checkpoint}.title`)}
					action={t(`checkpoints.${item.checkpoint}.action`)}
					completed={completed.data?.workspace.checkpoints?.some(
						(checkpoint) => checkpoint?.key === item.checkpoint && checkpoint?.completedAt,
					)}
				/>
			))}
		</>
	);
}
